import React from 'react';

interface PremiumHintProps {
    visible: boolean;
    title: string;
    lines: string[];
    icons?: React.ReactNode[];
    position?: 'sidebar' | 'top-center';
    onClose?: () => void;
    maskingDelay?: number; // ms to keep hint invisible when it first appears
}

const BattleLogHint: React.FC<PremiumHintProps> = ({
    visible,
    title,
    lines,
    icons = [],
    position = 'sidebar',
    onClose,
    maskingDelay = 0,
}) => {
    const [isRevealed, setIsRevealed] = React.useState(false);
    const [isReadyForNext, setIsReadyForNext] = React.useState(false);
    const [prevMaskingDelay, setPrevMaskingDelay] = React.useState(maskingDelay);
    const maskTimerRef = React.useRef<number>(0);
    const wasSuppressedRef = React.useRef(false);

    // Reset readiness if penalty increases
    if (maskingDelay > prevMaskingDelay) {
        setIsReadyForNext(false);
        setPrevMaskingDelay(maskingDelay);
    } else if (maskingDelay < prevMaskingDelay) {
        setPrevMaskingDelay(maskingDelay);
    }

    React.useEffect(() => {
        if (visible) {
            if (maskingDelay > 0 && !isReadyForNext) {
                // If we are masked and not ready, hide and start/wait for timer
                wasSuppressedRef.current = true;
                if (!maskTimerRef.current) {
                    maskTimerRef.current = window.setTimeout(() => {
                        setIsReadyForNext(true);
                        maskTimerRef.current = 0;
                    }, maskingDelay);
                }
            } else if (isReadyForNext || maskingDelay === 0) {
                // Show only if this specific trigger didn't start during masking
                if (!wasSuppressedRef.current) {
                    setIsRevealed(true);
                }
            }
        } else {
            // Clean up when hidden
            window.clearTimeout(maskTimerRef.current);
            maskTimerRef.current = 0;
            setIsRevealed(false);
            wasSuppressedRef.current = false;
        }
        return () => window.clearTimeout(maskTimerRef.current);
    }, [visible, maskingDelay, isReadyForNext]);

    const effectiveVisible = visible && isRevealed;
    const positionClasses = position === 'top-center'
        ? 'top-8 left-1/2 -translate-x-1/2 w-auto min-w-[320px] max-w-lg'
        : 'top-20 right-4 w-80';

    return (
        <div className={`
            fixed z-[120] ${positionClasses}
            transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)]
            ${effectiveVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-8 scale-90'}
            pointer-events-none
        `}>
            {/* Sunset Gradient Container */}
            <div className={`
                relative overflow-hidden rounded-2xl p-0.5
                bg-gradient-to-br from-orange-400 via-rose-500 to-indigo-600
                shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6),0_0_30px_rgba(244,63,94,0.3)]
                before:absolute before:inset-0 before:bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] before:opacity-10
            `}>

                {/* Shimmer Effect */}
                <div className="absolute inset-0 w-[200%] h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-[shimmer_3s_infinite]"
                    style={{ animation: 'shimmer 4s infinite linear' }} />

                {onClose && (
                    <button
                        onClick={onClose}
                        className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white/70 hover:text-white flex items-center justify-center transition-all duration-200 z-[130] group pointer-events-auto"
                        title="Close hint"
                    >
                        <svg className="w-4 h-4 transform group-hover:rotate-90 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}

                <div className={`relative bg-slate-950/20 backdrop-blur-3xl rounded-[14px] p-4 flex flex-col gap-3 ${effectiveVisible ? 'pointer-events-auto' : 'pointer-events-none'}`}>

                    {/* Header Section */}
                    <div className="flex items-center gap-4 border-b border-white/10 pb-3">
                        <h3 className="font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 to-orange-200 text-xl tracking-tight leading-tight drop-shadow-sm">
                            {title}
                        </h3>
                    </div>

                    {/* Content Section */}
                    <div className={`flex flex-col gap-2 ${position === 'top-center' ? 'items-start text-left' : ''}`}>
                        {lines.map((line, i) => {
                            const isSpecial = line.includes('Essence') || line.includes('Shield') || line.includes('Pass') || line.includes('reminders');
                            return (
                                <p
                                    key={i}
                                    className={`
                                        text-sm leading-relaxed 
                                        ${isSpecial ? 'text-yellow-200 font-bold italic' : 'text-white/90 font-medium'}
                                        transition-colors duration-300
                                    `}
                                >
                                    {line}
                                </p>
                            );
                        })}
                    </div>
                </div>

                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-yellow-400/10 blur-3xl rounded-full -mr-12 -mt-12" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-indigo-500/10 blur-3xl rounded-full -ml-12 -mb-12" />
            </div>

            <style>{`
                @keyframes shimmer {
                    0% { transform: translateX(-150%) skewX(-20deg); }
                    100% { transform: translateX(150%) skewX(-20deg); }
                }
            `}</style>
        </div>
    );
};

export default BattleLogHint;
